import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Page,
  PageHeader,
  Button,
  Flex,
  Text,
  LoadingOrbit,
  TagBadge,
  badgeVariantToTagColor,
  Markdown,
  CollapsibleSection,
  Card,
  CardContent,
} from '@chatsdet/ui';
import { GithubLogo, ArrowLeft, ArrowSquareOut } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { safeHref } from '../../utils/safeHref';
import { usePrReview, VERDICT_META, STATUS_META, TRIGGER_LABEL, REPO_FULL_NAME_RE } from '../../features/pr-reviews';

/**
 * AI PR Review detail — a look-back at a single review attempt: what the
 * agent posted to GitHub, plus the raw report payload for debugging. This
 * page is read-only; there is no re-run/edit action here.
 */
export function PrReviewDetail() {
  const navigate = useNavigate();
  const { prReviewId } = useParams({ from: '/pr-reviews/$prReviewId' });
  const reviewQuery = usePrReview(prReviewId);
  const review = reviewQuery.data?.data;

  useBreadcrumbs([
    { label: 'PR Reviews', onPress: () => navigate({ to: '/pr-reviews' }) },
    { label: review ? `#${review.prNumber} ${review.prTitle}` : 'Review' },
  ]);

  if (reviewQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <Page>
        <PageHeader title="PR Review Not Found" description="This review could not be found." />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This review may have been deleted, or its linked repository was unlinked.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/pr-reviews' })}
            className="mt-4"
          >
            Back to PR Reviews
          </Button>
        </div>
      </Page>
    );
  }

  const repoIsValid = REPO_FULL_NAME_RE.test(review.repoFullName);
  const prHref = repoIsValid
    ? safeHref(`https://github.com/${review.repoFullName}/pull/${review.prNumber}`)
    : undefined;
  const githubReviewHref =
    repoIsValid && review.githubReviewId
      ? safeHref(
          `https://github.com/${review.repoFullName}/pull/${review.prNumber}#pullrequestreview-${review.githubReviewId}`
        )
      : undefined;

  const statusMeta = STATUS_META[review.status];

  return (
    <Page>
      <PageHeader
        icon={<GithubLogo size={24} />}
        eyebrow={
          <Flex gap="xs" align="center">
            <TagBadge name={statusMeta.label} color={badgeVariantToTagColor(statusMeta.variant)} hideDot />
            {review.verdict ? (
              <TagBadge
                name={VERDICT_META[review.verdict].label}
                color={badgeVariantToTagColor(VERDICT_META[review.verdict].variant)}
                hideDot
              />
            ) : null}
            <TagBadge name={TRIGGER_LABEL[review.trigger]} color="gray" hideDot />
          </Flex>
        }
        title={`#${review.prNumber} ${review.prTitle}`}
        description={
          <Text size="sm" className="text-muted-foreground">
            {review.repoFullName} · {new Date(review.createdAt).toLocaleString()}
          </Text>
        }
        actions={
          <Flex gap="sm">
            <Button
              variant="outline"
              size="sm"
              onPress={() => navigate({ to: '/pr-reviews' })}
              iconLeft={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            {prHref && (
              <Button
                variant="outline"
                size="sm"
                onPress={() => window.open(prHref, '_blank', 'noopener,noreferrer')}
                iconLeft={<GithubLogo size={16} />}
              >
                View PR
              </Button>
            )}
            {githubReviewHref && (
              <Button
                variant="primary"
                size="sm"
                onPress={() => window.open(githubReviewHref, '_blank', 'noopener,noreferrer')}
                iconRight={<ArrowSquareOut size={16} />}
              >
                View Posted Review
              </Button>
            )}
          </Flex>
        }
      />

      {review.error && (
        <Card className="mt-6 border-destructive/30">
          <CardContent>
            <Text size="sm" weight="medium" className="text-destructive">
              Run error
            </Text>
            <Text size="sm" className="text-muted-foreground mt-1">
              {review.error}
            </Text>
          </CardContent>
        </Card>
      )}

      {review.bodyMarkdown ? (
        <Card className="mt-6">
          <CardContent>
            <Markdown className="prose prose-sm max-w-none">{review.bodyMarkdown}</Markdown>
          </CardContent>
        </Card>
      ) : (
        !review.error && (
          <div className="mt-6 text-center py-12 border-2 border-dashed border-border rounded-lg">
            <Text className="text-muted-foreground">No review body was produced for this run.</Text>
          </div>
        )
      )}

      {review.reportJson !== null && review.reportJson !== undefined && (
        <div className="mt-6">
          <CollapsibleSection label="Raw report" defaultExpanded={false}>
            <pre className="text-xs bg-muted/40 rounded-md p-3 overflow-x-auto">
              {JSON.stringify(review.reportJson, null, 2)}
            </pre>
          </CollapsibleSection>
        </div>
      )}
    </Page>
  );
}
